import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { BedsTypeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import { MaterialId } from '@core/constants/Material';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
    IFormFieldDimensionDefaultValueKey,
} from '../../interfaces/Form';
import additionalDataFactory from './common/fieldsAdditionalData';
import fieldsContact from './common/fieldsContact';
import {
    allMelamine,
    allMetals,
    allWoods,
    getMaterialsWithExceptions,
} from './common/materialGroups';
import budgetMarksBuilder from './common/budgetMarksBuilder';

const bedMezzanineForm: IForm = {
    formId       : PublishedFormId.CustomMezzanine,
    type         : FormTypeId.Beds,
    thumbnailURL : '/resized-images/forms/bed-mezzanine.jpg',
    fieldsOrder  : [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.BedShapeDimension,
        IFormFieldKey.Material,
        IFormFieldKey.Dimension,
        IFormFieldKey.AddOns,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        bedShapeDimension: {
            shape: {
                value   : undefined,
                options : [
                    BedsTypeId.MezzanineSingle,
                    BedsTypeId.MezzanineDouble,
                ],
                required: true,
            },
            dimension: {
                options: {
                    [BedsTypeId.MezzanineSingle]: {
                        [FieldDimensionId.Width]: {
                            value         : undefined,
                            type          : IFormInputType.Range,
                            unit          : IDimensionUnit.Centimeters,
                            step          : 100,
                            range         : { min: 600, max: 800 },
                            required      : true,
                            defaultValue  : 700,
                            defaultValues : [
                                [IFormFieldDimensionDefaultValueKey.ChildBed, 700],
                                [IFormFieldDimensionDefaultValueKey.TeenagerBed, 900],
                                [IFormFieldDimensionDefaultValueKey.AdultBed, 800],
                            ],
                        },
                        [FieldDimensionId.Length]: {
                            value         : undefined,
                            type          : IFormInputType.Range,
                            unit          : IDimensionUnit.Centimeters,
                            step          : 100,
                            range         : { min: 1200, max: 2000 },
                            required      : true,
                            defaultValue  : 1400,
                            defaultValues : [
                                [IFormFieldDimensionDefaultValueKey.ChildBed, 1400],
                                [IFormFieldDimensionDefaultValueKey.TeenagerBed, 1900],
                                [IFormFieldDimensionDefaultValueKey.AdultBed, 2000],
                            ],
                        },
                    },
                    [BedsTypeId.MezzanineDouble]: {
                        [FieldDimensionId.Width]: {
                            value         : undefined,
                            type          : IFormInputType.Range,
                            unit          : IDimensionUnit.Centimeters,
                            step          : 100,
                            range         : { min: 1400, max: 2400 },
                            required      : true,
                            defaultValue  : 1600,
                            defaultValues : [
                                [IFormFieldDimensionDefaultValueKey.StandardBed, 1400],
                                [IFormFieldDimensionDefaultValueKey.QueenSizeBed, 1600],
                                [IFormFieldDimensionDefaultValueKey.KingSizeBed, 1800],
                                [IFormFieldDimensionDefaultValueKey.SuperKingSizeBed, 2200],
                            ],
                        },
                        [FieldDimensionId.Length]: {
                            value         : undefined,
                            type          : IFormInputType.Range,
                            unit          : IDimensionUnit.Centimeters,
                            step          : 100,
                            range         : { min: 1900, max: 2400 },
                            required      : true,
                            defaultValue  : 2000,
                            defaultValues : [
                                [IFormFieldDimensionDefaultValueKey.StandardBed, 1900],
                                [IFormFieldDimensionDefaultValueKey.QueenSizeBed, 2000],
                                [IFormFieldDimensionDefaultValueKey.KingSizeBed, 2000],
                                [IFormFieldDimensionDefaultValueKey.SuperKingSizeBed, 2200],
                            ],
                        },
                    },
                },
            },
        },
        material: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions([
                    ...allWoods,
                    ...allMelamine,
                    ...allMetals,
                ],
                [
                    MaterialId.BlackLocust,
                    MaterialId.Iroko,
                    MaterialId.Douglas,
                    MaterialId.DouglasEngineered,
                    MaterialId.DouglasRecycled,
                    MaterialId.DouglasVeneer,
                    MaterialId.Larch,
                    MaterialId.LarchEngineered,
                    MaterialId.LarchRecycled,
                    MaterialId.LarchVeneer,
                    MaterialId.Padouk,
                    MaterialId.PadoukRecycled,
                    MaterialId.PadoukEngineered,
                    MaterialId.PadoukVeneer,
                    MaterialId.DriftWood,
                    MaterialId.DriftWoodRecycled,
                    MaterialId.Ipe,
                    MaterialId.IpeRecycled,
                    MaterialId.IpeEngineered,
                    MaterialId.IpeVeneer,
                    MaterialId.Garapa,
                    MaterialId.Rust,
                    MaterialId.Brass,
                    MaterialId.Iron,
                    MaterialId.ThermoLaqueredBlack,
                    MaterialId.ThermoLaqueredWhite,
                    MaterialId.SheetMetal,
                    MaterialId.StainlessSteel,
                    MaterialId.Zinc,
                    MaterialId.WroughtIron,
                ]),
                required: true,
            },
        },
        dimension: {
            [FieldDimensionId.Length]: {
                value         : undefined,
                type          : IFormInputType.Range,
                unit          : IDimensionUnit.Centimeters,
                step          : 100,
                range         : { min: 1200, max: 2800 },
                required      : true,
                defaultValue  : 1600,
                defaultValues : [
                    [IFormFieldDimensionDefaultValueKey.StandardBed, 2100],
                    [IFormFieldDimensionDefaultValueKey.QueenSizeBed, 2200],
                    [IFormFieldDimensionDefaultValueKey.KingSizeBed, 2200],
                    [IFormFieldDimensionDefaultValueKey.SuperKingSizeBed, 2400],
                ],
            },
            [FieldDimensionId.Width]: {
                value         : undefined,
                type          : IFormInputType.Range,
                unit          : IDimensionUnit.Centimeters,
                step          : 100,
                range         : { min: 700, max: 2800 },
                required      : true,
                defaultValue  : 800,
                defaultValues : [
                    [IFormFieldDimensionDefaultValueKey.StandardBed, 1600],
                    [IFormFieldDimensionDefaultValueKey.QueenSizeBed, 1800],
                    [IFormFieldDimensionDefaultValueKey.KingSizeBed, 2000],
                    [IFormFieldDimensionDefaultValueKey.SuperKingSizeBed, 2400],
                ],
            },
            [FieldDimensionId.Height]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 100,
                range        : { min: 1200, max: 2500 },
                required     : true,
                defaultValue : 1500,
            },
        },
        addOns: {
            [BedsTypeId.BedLighting]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
            [BedsTypeId.OfficeUnderMezzanine]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
            [BedsTypeId.StorageUnderMezzanine]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
        },
        additionalData: additionalDataFactory({
            budgetMin          : 2000,
            budgetMax          : 15000,
            budgetDefaultValue : 4000,
            marks              : budgetMarksBuilder(2000, 100, 8000, 250, 15000),
        }),
        contact: fieldsContact,
    },
};

export default bedMezzanineForm;
